/**
 * OrderCard Component
 * Display order information
 */

import React from 'react';
import styled from 'styled-components';
import Card from './Card';

const OrderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--spacing-md);
`;

const OrderId = styled.div`
  font-size: var(--font-md);
  font-weight: var(--font-semibold);
  color: var(--text-primary);
`;

const OrderDate = styled.div`
  font-size: var(--font-sm);
  color: var(--text-secondary);
`;

const OrderStatus = styled.span<{ status: string }>`
  padding: var(--spacing-xs) var(--spacing-md);
  border-radius: var(--radius-md);
  font-size: var(--font-sm);
  font-weight: var(--font-medium);
  background: ${({ status }) => {
    switch (status) {
      case 'completed':
        return 'rgba(76, 175, 80, 0.2)';
      case 'pending':
        return 'rgba(255, 193, 7, 0.2)';
      case 'cancelled':
        return 'rgba(255, 59, 48, 0.2)';
      default:
        return 'rgba(142, 142, 147, 0.2)';
    }
  }};
  color: ${({ status }) => {
    switch (status) {
      case 'completed':
        return 'var(--accent-green)';
      case 'pending':
        return '#FFC107';
      case 'cancelled':
        return 'var(--accent-red)';
      default:
        return 'var(--text-secondary)';
    }
  }};
`;

const OrderItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-md);
`;

const OrderItem = styled.div`
  font-size: var(--font-sm);
  color: var(--text-secondary);
`;

const OrderFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: var(--spacing-md);
  border-top: 1px solid var(--border-color);
`;

const OrderTotal = styled.div`
  font-size: var(--font-lg);
  font-weight: var(--font-bold);
  color: var(--accent-green);
`;

interface OrderCardProps {
  orderId: string;
  date: string;
  status: 'pending' | 'completed' | 'cancelled' | 'processing';
  items: string[];
  total: string;
  onClick?: () => void;
}

const OrderCard: React.FC<OrderCardProps> = ({ orderId, date, status, items, total, onClick }) => {
  return (
    <Card clickable onClick={onClick}>
      <OrderHeader>
        <div>
          <OrderId>Order #{orderId}</OrderId>
          <OrderDate>{date}</OrderDate>
        </div>
        <OrderStatus status={status}>{status}</OrderStatus>
      </OrderHeader>
      <OrderItems>
        {items.slice(0, 2).map((item, index) => (
          <OrderItem key={index}>{item}</OrderItem>
        ))}
        {items.length > 2 && <OrderItem>+{items.length - 2} more items</OrderItem>}
      </OrderItems>
      <OrderFooter>
        <OrderTotal>{total}</OrderTotal>
      </OrderFooter>
    </Card>
  );
};

export default OrderCard;
